import React, { useMemo, useState } from "react";

/**
 * Single-line text input with @mention autocomplete.
 * `members` = [{ user_id, name }]. Mentions are matched server-side by `@Name`.
 */
export function MentionTextInput({ value, onChange, onEnter, members = [], placeholder, testid, autoFocus }) {
  const [showSug, setShowSug] = useState(false);

  const activeAt = useMemo(() => {
    const at = value.lastIndexOf("@");
    if (at === -1) return null;
    const q = value.slice(at + 1);
    if (q.length > 24) return null;
    return { at, q };
  }, [value]);

  const suggestions = useMemo(() => {
    if (!activeAt) return [];
    const q = activeAt.q.toLowerCase();
    const matches = members.filter((m) => m.name.toLowerCase().startsWith(q));
    // hide if the only match is already fully typed
    if (matches.length === 1 && matches[0].name.toLowerCase() === q) return [];
    return matches.slice(0, 6);
  }, [activeAt, members]);

  const open = showSug && suggestions.length > 0;

  const pick = (m) => {
    const before = value.slice(0, activeAt.at);
    onChange(`${before}@${m.name} `);
    setShowSug(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter") {
      if (open) {
        e.preventDefault();
        pick(suggestions[0]);
      } else {
        onEnter && onEnter(e);
      }
    } else if (e.key === "Escape") {
      setShowSug(false);
    }
  };

  return (
    <div className="relative flex-1">
      <input
        data-testid={testid}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => { onChange(e.target.value); setShowSug(true); }}
        onKeyDown={handleKey}
        onBlur={() => setTimeout(() => setShowSug(false), 150)}
        placeholder={placeholder}
        className="w-full bg-black/40 border border-zinc-800 px-3 py-2.5 text-sm focus:border-primary-accent outline-none"
      />
      {open && (
        <div className="absolute bottom-full mb-1 left-0 z-50 min-w-[200px] surface-card py-1" data-testid="mention-suggestions">
          {suggestions.map((m) => (
            <button
              key={m.user_id}
              type="button"
              data-testid={`mention-option-${m.user_id}`}
              onMouseDown={(e) => { e.preventDefault(); pick(m); }}
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-white/5 text-zinc-200"
            >
              <span className="text-primary-accent">@</span>{m.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Render post content, highlighting any @Name that matches a known member. */
export function renderWithMentions(content, names = []) {
  if (!names.length) return content;
  const escaped = names
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`@(${escaped.join("|")})`, "g");
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) parts.push(content.slice(last, m.index));
    parts.push(
      <span key={m.index} className="text-primary-accent font-medium">{m[0]}</span>
    );
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push(content.slice(last));
  return parts;
}
